import { SiteSurvey, Customer, Installation } from '../../../shared/schema';
import { normalizeSurveyPanels } from '../../../shared/site-survey-panels';
import { buildWhat3wordsMapUrl, formatWhat3wordsAddress } from '../../../shared/what3words';

interface SurveyPrintData {
  survey: Partial<SiteSurvey>;
  customer?: Partial<Customer> | null;
  installation?: Partial<Installation> | null;
  photos?: Record<string, { url: string; photoType: string }>;
}

const PRINT_IMAGE_FETCH_TIMEOUT_MS = 30_000;
const PRINT_IMAGE_READER_TIMEOUT_MS = 30_000;

export class PrintService {
  /**
   * Escape unsafe HTML characters
   */
  private static escapeHtml(input: string): string {
    return input
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  /**
   * Convert text (plain or safe editor HTML) to safe HTML for PDF.
   * - Preserves <mark> from the editor and converts them to <mark class="pdf-highlight">.
   * - Strips unknown HTML while preserving line breaks.
   */
  static formatText(value: any): string {
    if (value === null || value === undefined) return '';
    const raw = String(value);

    // Helper to normalize any <mark ...> to <mark class="pdf-highlight">
    const normalizeMarks = (html: string) =>
      html
        .replace(/<mark[^>]*>/gi, '<mark class="pdf-highlight">')
        .replace(/<\s*\/\s*mark\s*>/gi, '</mark>');

    // If content contains HTML (e.g., from HighlightEditor), sanitize conservatively
    if (raw.includes('<')) {
      // Preserve mark and line-break semantics using placeholders
      let placeholder = raw
        // Treat </div> as newlines and strip opening <div>
        .replace(/<\s*\/\s*div\s*>/gi, '\n')
        .replace(/<\s*div[^>]*>/gi, '')
        // Normalize different <br> variants to newline
        .replace(/<\s*br\s*\/?\s*>/gi, '\n')
        // Replace mark tags with placeholders
        .replace(/<mark[^>]*>/gi, '[[MARK_START]]')
        .replace(/<\s*\/\s*mark\s*>/gi, '[[MARK_END]]');

      // Strip any other tags
      placeholder = placeholder.replace(/<[^>]+>/g, '');

      // Escape remaining text
      let safe = this.escapeHtml(placeholder);

      // Restore placeholders as real mark tags and line breaks
      safe = safe
        .replace(/\[\[MARK_START\]\]/g, '<mark class="pdf-highlight">')
        .replace(/\[\[MARK_END\]\]/g, '</mark>')
        .replace(/\r?\n/g, '<br />');

      // Ensure mark tags are normalized
      return normalizeMarks(safe);
    }

    // Plain text path: escape and convert newlines
    const escaped = this.escapeHtml(raw);
    return escaped.replace(/\r?\n/g, '<br />');
  }

  /**
   * Format a field and optionally wrap with a highlight if the field is marked in metadata.
   */
  private static formatField(
    value: any,
    fieldKey?: string,
    highlightedFields?: Record<string, boolean>,
    defaultValue: string = 'Not provided'
  ): string {
    if (value === null || value === undefined || value === '') return defaultValue;
    const html = PrintService.formatText(value);
    if (fieldKey && highlightedFields && highlightedFields[fieldKey]) {
      return `<mark class="pdf-highlight">${html}</mark>`;
    }
    return html;
  }

  /**
   * Render a single info-item div only when the value is present.
   * Returns empty string for null/undefined/empty values, skipping "Not provided" placeholders.
   */
  private static renderInfoItem(
    label: string,
    value: any,
    fieldKey?: string,
    highlightedFields?: Record<string, boolean>
  ): string {
    if (value === null || value === undefined || value === '') return '';
    const formatted = PrintService.formatField(value, fieldKey, highlightedFields);
    return `<div class="info-item">
      <span class="info-label">${label}</span>
      <span class="info-value">${formatted}</span>
    </div>`;
  }

  private static renderPanelsTable(survey: any): string {
    const panels = normalizeSurveyPanels(survey);

    if (panels.length === 0) return '';

    const panelRows = panels.map((panel, index) => {
      const quantity = panel.quantity > 0 ? panel.quantity : '';

      return `
        <tr>
          <td style="padding: 8px; border: 1px solid #e5e7eb;">${index + 1}</td>
          <td style="padding: 8px; border: 1px solid #e5e7eb;">
            ${panel.highlighted
              ? `<mark class="pdf-highlight">${PrintService.formatText(panel.description)}</mark>`
              : PrintService.formatText(panel.description)}
          </td>
          <td style="padding: 8px; border: 1px solid #e5e7eb; text-align: center;">${quantity}</td>
        </tr>
      `;
    }).join('');

    return `
      <div class="info-item" style="grid-column: span 2;">
        <span class="info-label">Solar Panels</span>
        <table style="width: 100%; border-collapse: collapse; margin-top: 8px;">
          <thead>
            <tr style="background-color: #f9fafb;">
              <th style="padding: 8px; border: 1px solid #e5e7eb; text-align: left; width: 50px;">#</th>
              <th style="padding: 8px; border: 1px solid #e5e7eb; text-align: left;">Panel Type</th>
              <th style="padding: 8px; border: 1px solid #e5e7eb; text-align: center; width: 100px;">Quantity</th>
            </tr>
          </thead>
          <tbody>
            ${panelRows}
          </tbody>
        </table>
      </div>
    `;
  }

  /**
   * Fetch survey data with photos for printing
   */
  static async fetchSurveyData(surveyId: string): Promise<SurveyPrintData> {
    console.log('[PrintService] Fetching survey data for ID:', surveyId);

    // Fetch survey data
    const surveyResponse = await fetch(`/api/site-surveys/${surveyId}`, {
      credentials: 'include',
    });

    if (!surveyResponse.ok) {
      const errorText = await surveyResponse.text();
      console.error('[PrintService] Failed to fetch survey:', surveyResponse.status, errorText);
      throw new Error(`Failed to fetch survey data: ${surveyResponse.status}`);
    }

    // Extract the survey from the response structure
    const surveyData = await surveyResponse.json();
    console.log('[PrintService] Survey response structure:', {
      hasSuccess: 'success' in surveyData,
      hasSurvey: 'survey' in surveyData,
      keys: Object.keys(surveyData).slice(0, 10),
    });

    const survey = surveyData.survey || surveyData; // Handle both wrapped and unwrapped responses

    // Photos are already included in the survey response
    const photos = survey.photos || {};
    console.log('[PrintService] Found photos:', Object.keys(photos).length);

    // Fetch customer data if available
    let customer = null;
    if (survey.customerId) {
      try {
        const customerResponse = await fetch(`/api/customers/${survey.customerId}`, {
          credentials: 'include',
        });
        if (customerResponse.ok) {
          customer = await customerResponse.json();
          console.log('[PrintService] Customer data fetched');
        }
      } catch (error) {
        console.warn('[PrintService] Failed to fetch customer data:', error);
      }
    }

    // Fetch installation data if available
    let installation = null;
    if (survey.installationId) {
      try {
        const installationResponse = await fetch(`/api/installations/${survey.installationId}`, {
          credentials: 'include',
        });
        if (installationResponse.ok) {
          installation = await installationResponse.json();
          console.log('[PrintService] Installation data fetched');
        }
      } catch (error) {
        console.warn('[PrintService] Failed to fetch installation data:', error);
      }
    }

    console.log('[PrintService] Data fetch complete:', {
      surveyId: survey.id,
      hasCustomer: !!customer,
      hasInstallation: !!installation,
      photoCount: Object.keys(photos).length,
    });

    return { survey, customer, installation, photos };
  }

  /**
   * Convert image URL to base64 data URI
   */
  static async imageToBase64(url: string): Promise<string> {
    try {
      // Ensure URL is absolute
      const absoluteUrl = url.startsWith('http') ? url : `${window.location.origin}${url}`;

      console.log('[PrintService] Fetching image from:', absoluteUrl);

      const controller = new AbortController();
      const fetchTimeoutId = setTimeout(() => {
        controller.abort('Print image fetch timed out');
      }, PRINT_IMAGE_FETCH_TIMEOUT_MS);

      let response: Response;
      try {
        response = await fetch(absoluteUrl, {
          method: 'GET',
          headers: {
            Accept: 'image/*, application/octet-stream',
          },
          credentials: 'include',
          signal: controller.signal,
        });
      } finally {
        clearTimeout(fetchTimeoutId);
      }

      if (!response.ok) {
        console.error(
          `[PrintService] Failed to fetch image: ${response.status} ${response.statusText}`
        );
        return '';
      }

      const contentType = response.headers.get('content-type');
      console.log('[PrintService] Image content type:', contentType);

      const blob = await response.blob();
      console.log('[PrintService] Image blob size:', blob.size);

      if (blob.size === 0) {
        console.error('[PrintService] Empty image blob received');
        return '';
      }

      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        let settled = false;
        const readerTimeoutId = setTimeout(() => {
          if (settled) return;
          settled = true;
          reader.onloadend = null;
          reader.onerror = null;
          if (reader.readyState === 1) {
            reader.abort();
          }
          reject(new Error('Timed out converting image to base64'));
        }, PRINT_IMAGE_READER_TIMEOUT_MS);

        const cleanup = () => clearTimeout(readerTimeoutId);

        reader.onloadend = () => {
          if (settled) return;
          settled = true;
          cleanup();
          const result = reader.result as string;
          console.log('[PrintService] Base64 data URI length:', result.length);
          resolve(result);
        };
        reader.onerror = (error) => {
          if (settled) return;
          settled = true;
          cleanup();
          console.error('[PrintService] FileReader error:', error);
          reject(error);
        };
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.error('[PrintService] Failed to convert image to base64:', error);
      return '';
    }
  }

  /**
   * Extract surname from full name for PDF naming
   */
  private static extractSurname(fullName: string | undefined | null): string {
    if (!fullName || fullName.trim() === '') return 'Customer';
    
    // Split the name by spaces and get the last part
    const nameParts = fullName.trim().split(/\s+/);
    const surname = nameParts[nameParts.length - 1];
    
    // Return the surname or 'Customer' as fallback
    return surname || 'Customer';
  }

  /**
   * Generate print-optimized HTML for survey
   */
  static async generatePrintHTML(
    data: SurveyPrintData,
    onProgress?: (progress: number, message: string) => void
  ): Promise<string> {
    const { survey, customer, installation, photos } = data;

    console.log(
      '[PrintService] Generating HTML with photos:',
      photos ? Object.keys(photos).length : 0
    );

    // Convert all photos to base64 in parallel for better performance
    const photoBase64Map: Record<string, string> = {};
    if (photos) {
      console.log('[PrintService] Processing photos for base64 conversion');
      onProgress?.(10, 'Processing images...');

      const photoEntries = Object.entries(photos).filter(([_, photo]) => photo.url);
      const totalPhotos = photoEntries.length;
      let processedPhotos = 0;

      // Process photos in parallel with batching to avoid overwhelming the browser
      const batchSize = 3; // Process 3 images at a time
      for (let i = 0; i < photoEntries.length; i += batchSize) {
        const batch = photoEntries.slice(i, i + batchSize);

        const batchPromises = batch.map(async ([type, photo]) => {
          console.log(`[PrintService] Processing photo type: ${type}`);
          try {
            const base64 = await this.imageToBase64(photo.url);
            if (base64) {
              photoBase64Map[type] = base64;
              console.log(`[PrintService] Successfully converted ${type} to base64`);
            }
          } catch (error) {
            console.warn(`[PrintService] Failed to convert ${type} to base64:`, error);
          }
          processedPhotos++;
          const progress = 10 + Math.floor((processedPhotos / totalPhotos) * 80);
          onProgress?.(progress, `Processing image ${processedPhotos} of ${totalPhotos}...`);
        });

        await Promise.all(batchPromises);
      }

      console.log(
        `[PrintService] Converted ${Object.keys(photoBase64Map).length} photos to base64`
      );
    }

    onProgress?.(90, 'Generating PDF...');

    // Extract surname for PDF naming
    // Use survey clientName first (as entered during survey) then fall back to customer database record
    const customerSurname = this.extractSurname(survey?.clientName || customer?.name);

    // Generate HTML with embedded styles and images
    return `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Survey ${customerSurname}</title>
  <style>
    ${this.getPrintStyles()}
  </style>
</head>
<body>
  <div class="print-container">
    ${this.generateHeader()}
    ${survey?.mainFuseSize || (Array.isArray(survey?.roofs) && survey.roofs.length > 0) || survey?.dcStrings || survey?.inverterDb ? `<div class="section-legend">
      <div class="legend-item">
        <div class="legend-swatch legend-swatch-electrical"></div>
        <span>AC Work</span>
      </div>
      <div class="legend-item">
        <div class="legend-swatch legend-swatch-dc"></div>
        <span>DC Work</span>
      </div>
      <div class="legend-item">
        <div class="legend-swatch legend-swatch-roof"></div>
        <span>Roof Work</span>
      </div>
      <div class="legend-item">
        <div class="legend-swatch legend-swatch-limitation"></div>
        <span>Export &amp; Inverter Limitations</span>
      </div>
    </div>` : ''}
    ${this.generateProjectInfo(survey, customer, installation, photos, photoBase64Map)}
    ${this.generateMeterDNOInfo(survey, photos, photoBase64Map)}
    ${this.generateRoofDetails(survey, photos, photoBase64Map)}
    ${this.generateProposedSystem(survey)}
    ${this.generateACInstallation(survey, photos, photoBase64Map)}
    ${this.generateEquipmentLocation(survey, photos, photoBase64Map)}
    ${this.generateDCAdditionalWorks(survey, photos, photoBase64Map)}
    ${this.generateEarthingGroundWorks(survey, photos, photoBase64Map)}
    ${this.generateSurveyMetadata(survey)}
    ${this.generateNotesObservations(survey)}
    ${this.generateFooter()}
  </div>
</body>
</html>`;
  }

  /**
   * Get print-optimized CSS styles
   */
  private static getPrintStyles(): string {
    return `
    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }

    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Helvetica Neue', Arial, sans-serif;
      font-size: 10pt;
      line-height: 1.4;
      color: #333;
      background: white;
    }

    .print-container {
      width: 100%;
      max-width: 210mm;
      margin: 0 auto;
      padding: 8mm;
    }

    /* Header */
    .header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding-bottom: 10px;
      border-bottom: 2px solid #004AAD;
      margin-bottom: 12px;
    }

    .logo-text {
      font-size: 22pt;
      font-weight: bold;
    }

    .logo-dom { color: #004AAD; }
    .logo-elec { color: #61B33B; }

    /* Sections - Adaptive layout */
    .section {
      margin-bottom: 6px;
      break-inside: auto;
    }

    .section-title {
      font-size: 12pt;
      font-weight: bold;
      color: #004AAD;
      margin-bottom: 6px;
      padding-bottom: 2px;
      border-bottom: 1px solid #e0e0e0;
    }

    /* Color-coded section borders for trade identification */
    .section-roof {
      border-left: 4px solid #EAB308;
      padding-left: 10px;
      margin-left: -2px;
    }

    .section-roof .section-title {
      color: #A16207;
    }

    .section-electrical {
      border-left: 4px solid #DC2626;
      padding-left: 10px;
      margin-left: -2px;
    }

    .section-electrical .section-title {
      color: #B91C1C;
    }

    .section-dc {
      border-left: 4px solid #2563EB;
      padding-left: 10px;
      margin-left: -2px;
    }

    .section-dc .section-title {
      color: #1D4ED8;
    }

    /* Limitation callout box */
    .limitation-callout {
      background: #FEF3C7;
      border: 2px solid #D97706;
      border-radius: 4px;
      padding: 8px 10px;
      margin: 8px 0;
    }

    .limitation-callout-title {
      font-size: 10pt;
      font-weight: bold;
      color: #92400E;
      margin-bottom: 4px;
      text-transform: uppercase;
    }

    .limitation-callout .info-grid {
      margin-bottom: 0;
    }

    /* Section legend */
    .section-legend {
      display: flex;
      gap: 16px;
      margin-bottom: 12px;
      padding: 6px 10px;
      background: #f9f9f9;
      border-radius: 4px;
      font-size: 9pt;
    }

    .legend-item {
      display: flex;
      align-items: center;
      gap: 6px;
    }

    .legend-swatch {
      width: 14px;
      height: 14px;
      border-radius: 2px;
      flex-shrink: 0;
    }

    .legend-swatch-roof {
      background: #EAB308;
    }

    .legend-swatch-electrical {
      background: #DC2626;
    }

    .legend-swatch-dc {
      background: #2563EB;
    }

    .legend-swatch-limitation {
      background: #D97706;
    }

    /* Info Grid - Flexible layout */
    .info-grid {
      display: flex;
      flex-wrap: wrap;
      gap: 4px;
      margin-bottom: 4px;
    }

    .info-item {
      flex: 1 1 45%;
      min-width: 0;
    }

    .info-label {
      font-size: 9pt;
      color: #666;
      text-transform: uppercase;
      display: block;
    }

    .info-value {
      font-size: 11pt;
      color: #333;
      font-weight: 500;
    }

    /* Highlight style for printed output */
    mark.pdf-highlight {
      background: #fff59e; /* soft yellow */
      padding: 0 1px;
      border-radius: 1px;
    }

    /* Photo Gallery - Adaptive grid */
    .photo-gallery {
      margin-top: 10px;
    }

    .photo-grid {
      display: flex;
      flex-wrap: wrap;
      gap: 4px;
      margin-top: 4px;
    }

    .photo-item {
      flex: 0 1 calc(50% - 3px);
      border: 1px solid #e0e0e0;
      border-radius: 4px;
      overflow: hidden;
      break-inside: avoid;
    }

    .photo-label {
      background: #f5f5f5;
      padding: 4px 6px;
      font-size: 9pt;
      font-weight: 600;
      color: #666;
    }

    .photo-container {
      display: flex;
      align-items: center;
      justify-content: center;
      background: #fafafa;
      padding: 8px;
      max-height: 200px;
    }

    .photo-container img {
      max-width: 100%;
      max-height: 180px;
      object-fit: contain;
      display: block;
    }

    /* Large photo item - full width, half-page height */
    .photo-item-large {
      flex: 0 1 100%;
      border: 1px solid #e0e0e0;
      border-radius: 4px;
      overflow: hidden;
      break-inside: avoid;
    }

    .photo-item-large .photo-container {
      max-height: 520px;
      padding: 12px;
    }

    .photo-item-large .photo-container img {
      max-width: 100%;
      max-height: 500px;
      object-fit: contain;
    }

    .photo-placeholder {
      color: #999;
      font-size: 9pt;
      text-align: center;
      padding: 6px;
    }

    /* Notes */
    .notes-section {
      background: #f9f9f9;
      padding: 6px 8px;
      border-radius: 4px;
      margin-top: 6px;
    }

    .notes-content {
      white-space: pre-wrap;
      color: #555;
      font-size: 9pt;
    }


    /* Print specific */
    @media print {
      body {
        font-size: 9pt;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }

      .print-container {
        padding: 0;
      }

      .section {
        page-break-inside: auto;
      }
      
      .section-title {
        page-break-after: avoid;
        break-after: avoid;
      }

      .section-roof,
      .section-electrical,
      .section-dc,
      .limitation-callout,
      .section-legend,
      .legend-swatch {
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }

      .info-item { break-inside: avoid; }
      .info-grid { break-inside: avoid; }
      tr { break-inside: avoid; }
      thead { break-after: avoid; }
      .limitation-callout { break-inside: avoid; }
      .notes-section { break-inside: avoid; }
      .section-legend { break-inside: avoid; }
      h3 { break-after: avoid; }

      .photo-gallery {
        break-before: page;
      }

      .photo-item {
        page-break-inside: avoid;
      }

      /* Ensure photos don't leave empty spaces */
      .photo-grid {
        display: flex;
        flex-wrap: wrap;
      }
      
      @page {
        size: A4;
        margin: 10mm;
      }
    }
  `;
  }

  private static generateHeader(): string {
    return `
    <div class="header">
      <div class="logo">
        <div class="logo-text">
          <span class="logo-dom">Dom</span><span class="logo-elec">Elec</span>
        </div>
      </div>
    </div>`;
  }

  private static generateProjectInfo(
    survey: any,
    customer: any,
    installation: any,
    photos: any,
    photoBase64Map: Record<string, string>
  ): string {
    const highlighted = survey?.highlightedFields || {};
    const getValue = (
      value: any,
      fieldKey?: string,
      defaultValue: string = 'Not provided'
    ) => PrintService.formatField(value, fieldKey, highlighted, defaultValue);

    // Generate all project photos including general images
    const propertyPhotos = [];
    if (photoBase64Map['property_front']) {
      propertyPhotos.push({
        type: 'property_front',
        label: 'Property Front View',
        base64: photoBase64Map['property_front'],
      });
    }
    if (photoBase64Map['property_rear']) {
      propertyPhotos.push({
        type: 'property_rear',
        label: 'Property Rear View',
        base64: photoBase64Map['property_rear'],
      });
    }
    if (photoBase64Map['property_side']) {
      propertyPhotos.push({
        type: 'property_side',
        label: 'Property Side View',
        base64: photoBase64Map['property_side'],
      });
    }
    if (photoBase64Map['stringPlanFromOpenSolar']) {
      propertyPhotos.push({
        type: 'stringPlanFromOpenSolar',
        label: 'String Plan (OpenSolar)',
        base64: photoBase64Map['stringPlanFromOpenSolar'],
      });
    }
    if (photoBase64Map['aerialImageFromDrone']) {
      propertyPhotos.push({
        type: 'aerialImageFromDrone',
        label: 'Aerial View (Drone)',
        base64: photoBase64Map['aerialImageFromDrone'],
      });
    }
    if (photoBase64Map['generalImage1']) {
      propertyPhotos.push({
        type: 'generalImage1',
        label: 'General Photo 1',
        base64: photoBase64Map['generalImage1'],
      });
    }
    if (photoBase64Map['generalImage2']) {
      propertyPhotos.push({
        type: 'generalImage2',
        label: 'General Photo 2',
        base64: photoBase64Map['generalImage2'],
      });
    }
    if (photoBase64Map['generalImage3']) {
      propertyPhotos.push({
        type: 'generalImage3',
        label: 'General Photo 3',
        base64: photoBase64Map['generalImage3'],
      });
    }

    const what3wordsDisplayValue = formatWhat3wordsAddress(customer?.what3wordsAddress);
    const what3wordsMapUrl = buildWhat3wordsMapUrl(what3wordsDisplayValue);
    const what3wordsItem =
      what3wordsDisplayValue && what3wordsMapUrl
        ? `<div class="info-item">
        <span class="info-label">what3words Location</span>
        <span class="info-value">
          <a href="${this.escapeHtml(what3wordsMapUrl)}" target="_blank" rel="noopener noreferrer" style="color: inherit; font-family: monospace; font-weight: 600;">
            ${PrintService.formatText(what3wordsDisplayValue)}
          </a>
        </span>
      </div>`
        : '';

    const projectItems = [
      this.renderInfoItem(
        'Customer Name',
        customer?.name || survey?.clientName,
        undefined,
        highlighted
      ),
      this.renderInfoItem(
        'Installation Address',
        installation?.installationAddress || survey?.clientAddress,
        undefined,
        highlighted
      ),
      what3wordsItem,
      this.renderInfoItem('City', survey?.clientCity, undefined, highlighted),
      this.renderInfoItem('Post Code', survey?.clientPostCode, undefined, highlighted),
      this.renderInfoItem('Email', customer?.email || survey?.clientEmail, undefined, highlighted),
      this.renderInfoItem(
        'Phone',
        customer?.phone || survey?.clientTelephone,
        undefined,
        highlighted
      ),
      this.renderInfoItem('Property Type', survey?.propertyType, 'propertyType', highlighted),
      survey?.surveyDate
        ? `<div class="info-item">
        <span class="info-label">Survey Date</span>
        <span class="info-value">${PrintService.formatText(new Date(survey.surveyDate).toLocaleDateString('en-GB'))}</span>
      </div>`
        : '',
      this.renderInfoItem('Site Assessor', survey?.siteAssessorName, undefined, highlighted),
    ]
      .filter(Boolean)
      .join('');

    return `
    <div class="section">
      <h2 class="section-title">Project Information</h2>
      <div class="info-grid">${projectItems}</div>
      ${
        propertyPhotos.length > 0
          ? `
        <div class="photo-grid" style="margin-top: 8px;">
          ${propertyPhotos
            .map(
              (photo) => `
            <div class="${photo.type === 'stringPlanFromOpenSolar' ? 'photo-item-large' : 'photo-item'}">
              <div class="photo-label">${photo.label}</div>
              <div class="photo-container">
                <img src="${photo.base64}" alt="${photo.label}" />
              </div>
            </div>
          `
            )
            .join('')}
        </div>
      `
          : ''
      }
    </div>`;
  }

  private static generateMeterDNOInfo(
    survey: any,
    photos: any,
    photoBase64Map: Record<string, string>
  ): string {
    const highlighted = survey?.highlightedFields || {};
    const getValue = (
      value: any,
      fieldKey?: string,
      defaultValue: string = 'Not provided'
    ) => PrintService.formatField(value, fieldKey, highlighted, defaultValue);

    // Generate meter photos section - include all electrical photos
    const meterPhotos = [];
    if (photoBase64Map['photoIncomer']) {
      meterPhotos.push({
        type: 'photoIncomer',
        label: 'Incomer',
        base64: photoBase64Map['photoIncomer'],
      });
    }
    if (photoBase64Map['photoMeter']) {
      meterPhotos.push({
        type: 'photoMeter',
        label: 'Meter',
        base64: photoBase64Map['photoMeter'],
      });
    }
    if (photoBase64Map['photoMainDb']) {
      meterPhotos.push({
        type: 'photoMainDb',
        label: 'Main DB',
        base64: photoBase64Map['photoMainDb'],
      });
    }
    if (photoBase64Map['photoOther']) {
      meterPhotos.push({
        type: 'photoOther',
        label: 'Other Electrical',
        base64: photoBase64Map['photoOther'],
      });
    }
    if (photoBase64Map['meter_location']) {
      meterPhotos.push({
        type: 'meter_location',
        label: 'Meter Location',
        base64: photoBase64Map['meter_location'],
      });
    }
    if (photoBase64Map['meter_close_up']) {
      meterPhotos.push({
        type: 'meter_close_up',
        label: 'Meter Close-up',
        base64: photoBase64Map['meter_close_up'],
      });
    }
    if (photoBase64Map['consumer_unit']) {
      meterPhotos.push({
        type: 'consumer_unit',
        label: 'Consumer Unit',
        base64: photoBase64Map['consumer_unit'],
      });
    }
    if (photoBase64Map['consumer_unit_open']) {
      meterPhotos.push({
        type: 'consumer_unit_open',
        label: 'Consumer Unit (Open)',
        base64: photoBase64Map['consumer_unit_open'],
      });
    }

    const electricalItems = [
      this.renderInfoItem('Main Fuse Size', survey?.mainFuseSize, 'mainFuseSize', highlighted),
      this.renderInfoItem('Existing Demand (Amps)', survey?.existingDemand, 'existingDemand', highlighted),
      this.renderInfoItem('New Demand (Amps)', survey?.newDemand, 'newDemand', highlighted),
      this.renderInfoItem('Phases', survey?.phases, 'phases', highlighted),
      this.renderInfoItem('Earthing System', survey?.earthingSystem, 'earthingSystem', highlighted),
      this.renderInfoItem('AC SPD Present', survey?.acSpdPresent, 'acSpdPresent', highlighted),
      this.renderInfoItem('EICR', survey?.eicr, 'eicr', highlighted),
      this.renderInfoItem('Space Available for PV DB', survey?.spaceAvailableForPvDb, 'spaceAvailableForPvDb', highlighted),
      this.renderInfoItem('Water Bonding', survey?.bondingWater, 'bondingWater', highlighted),
      this.renderInfoItem('Gas Bonding', survey?.bondingGas, 'bondingGas', highlighted),
    ].filter(Boolean).join('');

    return `
    <div class="section section-electrical">
      <h2 class="section-title">Electrical Supply Information</h2>
      <div class="info-grid">${electricalItems}</div>
      ${
        meterPhotos.length > 0
          ? `
        <div class="photo-grid" style="margin-top: 8px;">
          ${meterPhotos
            .map(
              (photo) => `
            <div class="photo-item">
              <div class="photo-label">${photo.label}</div>
              <div class="photo-container">
                <img src="${photo.base64}" alt="${photo.label}" />
              </div>
            </div>
          `
            )
            .join('')}
        </div>
      `
          : ''
      }
    </div>`;
  }

  private static generateRoofDetails(
    survey: any,
    photos: any,
    photoBase64Map: Record<string, string>
  ): string {
    const highlighted = survey?.highlightedFields || {};
    const getValue = (
      value: any,
      fieldKey?: string,
      defaultValue: string = 'Not provided'
    ) => PrintService.formatField(value, fieldKey, highlighted, defaultValue);
    const roofs = survey?.roofs || [];
    const hasRoofData = Array.isArray(roofs) && roofs.length > 0;

    // Collect all roof photos
    const roofPhotos = [];
    const photoTypes = [
      'roof_front',
      'roof_rear',
      'roof_side',
      'roof_close_up',
      'roof_obstacles',
      'proposed_panel_area',
      'scaffoldPlanImage',
    ];
    photoTypes.forEach((type) => {
      if (photoBase64Map[type]) {
        roofPhotos.push({
          type,
          label: this.getPhotoLabel(type),
          base64: photoBase64Map[type],
        });
      }
    });

    // Also check for roofs.X.roofImageY and membrane patterns
    Object.keys(photoBase64Map).forEach((key) => {
      if (
        key.includes('roofs.') &&
        (key.includes('.roofImage') || key.includes('.membraneImage'))
      ) {
        const label = key.includes('membrane') ? 'Membrane Image' : 'Roof Image';
        roofPhotos.push({
          type: key,
          label: label,
          base64: photoBase64Map[key],
        });
      }
    });

    return `
    <div class="section section-roof">
      <h2 class="section-title">Roof Details</h2>
      ${
        hasRoofData
          ? roofs
              .map(
                (roof: any, index: number) => {
                  const roofItems = [
                    this.renderInfoItem('Type', roof?.roofType, undefined, highlighted),
                    this.renderInfoItem('Pitch', roof?.pitch, undefined, highlighted),
                    this.renderInfoItem('Membrane', roof?.membrane, undefined, highlighted),
                    this.renderInfoItem('Existing Faults', roof?.existingFaults, undefined, highlighted),
                    this.renderInfoItem('Optimisers', roof?.optimisers, undefined, highlighted),
                    this.renderInfoItem('Notes', roof?.notes, undefined, highlighted),
                  ].filter(Boolean).join('');
                  return `
        <h3 style="font-size: 12pt; margin-top: 10px;">Roof ${index + 1}</h3>
        <div class="info-grid">${roofItems}</div>
      `;
                }
              )
              .join('')
          : ''
      }
      ${(() => {
        const scaffoldItems = [
          this.renderInfoItem('Scaffolding Required', survey?.scaffolding, 'scaffolding', highlighted),
          this.renderInfoItem('Scaffolding Width', survey?.scaffoldingWidth, undefined, highlighted),
          this.renderInfoItem('Scaffolding Lifts', survey?.scaffoldingLifts, 'scaffoldingLifts', highlighted),
          this.renderInfoItem('Scope of Works (Roof)', survey?.scopeOfWorksRoof, undefined, highlighted),
        ].filter(Boolean).join('');
        return scaffoldItems ? `<div class="info-grid" style="margin-top: 10px;">${scaffoldItems}</div>` : '';
      })()}
      
      ${
        survey?.scaffoldingNotes
          ? `
        <div style="margin-top: 8px;">
          <strong>Scaffolding Notes:</strong> ${survey.scaffoldingNotes}
        </div>
      `
          : ''
      }
      ${
        roofPhotos.length > 0
          ? `
        <div class="photo-grid" style="margin-top: 8px;">
          ${roofPhotos
            .map(
              (photo) => `
            <div class="photo-item">
              <div class="photo-label">${photo.label}</div>
              <div class="photo-container">
                <img src="${photo.base64}" alt="${photo.label}" />
              </div>
            </div>
          `
            )
            .join('')}
        </div>
      `
          : ''
      }
    </div>`;
  }

  private static generateACInstallation(
    survey: any,
    photos: any,
    photoBase64Map: Record<string, string>
  ): string {
    const highlighted = survey?.highlightedFields || {};
    const getValue = (
      value: any,
      fieldKey?: string,
      defaultValue: string = 'Not provided'
    ) => PrintService.formatField(value, fieldKey, highlighted, defaultValue);

    // Cable route photos
    const cablePhotos = [];
    [
      'cableRouteImage1',
      'cableRouteImage2',
      'cable_route_exterior',
      'cable_route_interior',
    ].forEach((type) => {
      if (photoBase64Map[type]) {
        cablePhotos.push({
          type,
          label: this.getPhotoLabel(type),
          base64: photoBase64Map[type],
        });
      }
    });

    // Generate AC circuits table if data exists
    const hasCircuits =
      survey?.inverterCircuits &&
      Array.isArray(survey.inverterCircuits) &&
      survey.inverterCircuits.length > 0;
    let circuitsTable = '';
    if (hasCircuits) {
      const getCircuitPurposeLabel = (purpose: string) => {
        switch (purpose) {
          case 'INVERTER': return 'Inverter';
          case 'EV': return 'EV';
          case 'OTHER': return 'Other';
          default: return '-';
        }
      };
      
      const getCircuitValue = (
        value: any,
        index: number,
        fieldName: string,
        defaultValue: string = '-'
      ) => {
        const fieldKey = `inverterCircuits.${index}.${fieldName}`;
        return PrintService.formatField(value, fieldKey, highlighted, defaultValue);
      };
      
      circuitsTable = `
        <div style="margin-top: 10px;">
          <h3 style="font-size: 11pt; margin-bottom: 6px;">AC Circuits</h3>
          <table style="width: 100%; border-collapse: collapse; font-size: 9pt;">
            <thead>
              <tr style="background: #f5f5f5;">
                <th style="border: 1px solid #ddd; padding: 4px; text-align: left;">Purpose</th>
                <th style="border: 1px solid #ddd; padding: 4px; text-align: left;">Type</th>
                <th style="border: 1px solid #ddd; padding: 4px; text-align: left;">CCA</th>
                <th style="border: 1px solid #ddd; padding: 4px; text-align: left;">Length</th>
                <th style="border: 1px solid #ddd; padding: 4px; text-align: left;">Fault Protection</th>
                <th style="border: 1px solid #ddd; padding: 4px; text-align: left;">Device Type</th>
                <th style="border: 1px solid #ddd; padding: 4px; text-align: left;">Notes</th>
              </tr>
            </thead>
            <tbody>
              ${survey.inverterCircuits
                .map(
                  (circuit: any, index: number) => `
                <tr>
                  <td style="border: 1px solid #ddd; padding: 4px;">${getCircuitValue(circuit.circuitPurpose ? getCircuitPurposeLabel(circuit.circuitPurpose) : '-', index, 'circuitPurpose')}</td>
                  <td style="border: 1px solid #ddd; padding: 4px;">${getCircuitValue(circuit.type, index, 'type')}</td>
                  <td style="border: 1px solid #ddd; padding: 4px;">${getCircuitValue(circuit.cca, index, 'cca')}</td>
                  <td style="border: 1px solid #ddd; padding: 4px;">${getCircuitValue(circuit.length, index, 'length')}</td>
                  <td style="border: 1px solid #ddd; padding: 4px;">${getCircuitValue(circuit.faultProtection, index, 'faultProtection')}</td>
                  <td style="border: 1px solid #ddd; padding: 4px;">${getCircuitValue(circuit.deviceType, index, 'deviceType')}</td>
                  <td style="border: 1px solid #ddd; padding: 4px;">${getCircuitValue(circuit.notes, index, 'notes')}</td>
                </tr>
              `
                )
                .join('')}
            </tbody>
          </table>
        </div>
      `;
    }

    const acItems = [
      this.renderInfoItem('Inverter DB', survey?.inverterDb, 'inverterDb', highlighted),
      this.renderInfoItem('AC Size', survey?.acSize, 'acSize', highlighted),
      this.renderInfoItem('Switch/Fuse Required', survey?.switchFuseRequired, 'switchFuseRequired', highlighted),
    ].filter(Boolean).join('');

    return `
    <div class="section section-electrical">
      <h2 class="section-title">AC Installation</h2>
      <div class="info-grid">${acItems}</div>
      <div class="limitation-callout">
        <div class="limitation-callout-title">Export &amp; Inverter Limitations</div>
        <div class="info-grid">
          <div class="info-item">
            <span class="info-label">Export Limitation</span>
            <span class="info-value">${
              survey?.exportLimitation
                ? getValue(`${survey?.exportLimitation} kW`, 'exportLimitation')
                : 'Not provided'
            }</span>
          </div>
          <div class="info-item">
            <span class="info-label">Scope of Works (Inverter/AC)</span>
            <span class="info-value">${getValue(survey?.scopeOfWorksInverterAC)}</span>
          </div>
        </div>
      </div>
      ${circuitsTable}
      ${
        cablePhotos.length > 0
          ? `
        <div class="photo-grid" style="margin-top: 8px;">
          ${cablePhotos
            .map(
              (photo) => `
            <div class="photo-item">
              <div class="photo-label">${photo.label}</div>
              <div class="photo-container">
                <img src="${photo.base64}" alt="${photo.label}" />
              </div>
            </div>
          `
            )
            .join('')}
        </div>
      `
          : ''
      }
    </div>`;
  }

  private static generateEquipmentLocation(
    survey: any,
    photos: any,
    photoBase64Map: Record<string, string>
  ): string {
    const highlighted = survey?.highlightedFields || {};
    const getValue = (
      value: any,
      fieldKey?: string,
      defaultValue: string = 'Not provided'
    ) => PrintService.formatField(value, fieldKey, highlighted, defaultValue);

    // Equipment location photos
    const equipmentPhotos = [];
    ['proposedAreaPhoto', 'inverter_location', 'battery_location'].forEach((type) => {
      if (photoBase64Map[type]) {
        equipmentPhotos.push({
          type,
          label: this.getPhotoLabel(type),
          base64: photoBase64Map[type],
        });
      }
    });

    const hasDimensions = survey?.equipmentHeight || survey?.equipmentWidth;
    const equipmentItems = [
      this.renderInfoItem('Proposed Area', survey?.proposedArea, 'proposedArea', highlighted),
      this.renderInfoItem('Overboard Wall', survey?.overboardWall, 'overboardWall', highlighted),
      this.renderInfoItem('Canopy', survey?.canopy, 'canopy', highlighted),
      hasDimensions ? `<div class="info-item">
        <span class="info-label">Equipment Dimensions</span>
        <span class="info-value">H: ${PrintService.formatField(survey?.equipmentHeight, undefined, highlighted, 'N/A')} x W: ${PrintService.formatField(survey?.equipmentWidth, undefined, highlighted, 'N/A')}</span>
      </div>` : '',
    ].filter(Boolean).join('');

    return `
    <div class="section">
      <h2 class="section-title">Equipment Location</h2>
      <div class="info-grid">${equipmentItems}</div>
      ${
        survey?.proposedAreaNotes
          ? `
        <div style="margin-top: 8px;">
          <strong>Proposed Area Notes:</strong> ${survey.proposedAreaNotes}
        </div>
      `
          : ''
      }
      ${
        survey?.equipmentNotes
          ? `
        <div style="margin-top: 8px;">
          <strong>Equipment Notes:</strong> ${survey.equipmentNotes}
        </div>
      `
          : ''
      }
      ${
        equipmentPhotos.length > 0
          ? `
        <div class="photo-grid" style="margin-top: 8px;">
          ${equipmentPhotos
            .map(
              (photo) => `
            <div class="photo-item">
              <div class="photo-label">${photo.label}</div>
              <div class="photo-container">
                <img src="${photo.base64}" alt="${photo.label}" />
              </div>
            </div>
          `
            )
            .join('')}
        </div>
      `
          : ''
      }
    </div>`;
  }

  private static generateDCAdditionalWorks(
    survey: any,
    photos: any,
    photoBase64Map: Record<string, string>
  ): string {
    const highlighted = survey?.highlightedFields || {};
    const getValue = (
      value: any,
      fieldKey?: string,
      defaultValue: string = 'Not provided'
    ) => PrintService.formatField(value, fieldKey, highlighted, defaultValue);

    // DC route photos
    const dcPhotos = [];
    ['dcRouteImage1', 'dcRouteImage2', 'dcRouteImage3'].forEach((type) => {
      if (photoBase64Map[type]) {
        dcPhotos.push({
          type,
          label: this.getPhotoLabel(type),
          base64: photoBase64Map[type],
        });
      }
    });

    const dcItems = [
      this.renderInfoItem('DC Strings', survey?.dcStrings, 'dcStrings', highlighted),
      this.renderInfoItem('DC Cable CSA', survey?.dcCableCsa, 'dcCableCsa', highlighted),
      this.renderInfoItem('DC Longest Run', survey?.dcLongestRun, 'dcLongestRun', highlighted),
      this.renderInfoItem('Scope of Works (DC Runs)', survey?.scopeOfWorksDcRuns, undefined, highlighted),
    ].filter(Boolean).join('');

    return `
    <div class="section section-dc">
      <h2 class="section-title">DC &amp; Additional Works</h2>
      <div class="info-grid">${dcItems}</div>
      ${
        survey?.evChargerRequired === true
          ? `
        <div style="margin-top: 8px;">
          <strong>EV Charger:</strong> ${PrintService.formatText(survey.evChargerDetails || 'Required')}
        </div>
      `
          : ''
      }
      ${
        survey?.immersionCircuitRequired === true
          ? `
        <div style="margin-top: 8px;">
          <strong>Immersion Circuit:</strong> ${PrintService.formatText(survey.immersionCircuitDetails || 'Required')}
        </div>
      `
          : ''
      }
      ${
        survey?.birdguardRequired === true
          ? `
        <div style="margin-top: 8px;">
          <strong>Bird Guard:</strong> ${PrintService.formatText(survey.birdguardDetails || 'Required')}
        </div>
      `
          : ''
      }
      ${
        survey?.gatewayRequired === true
          ? `
        <div style="margin-top: 8px;">
          <strong>Gateway:</strong> ${PrintService.formatText(survey.gatewayDetails || 'Required')}
        </div>
      `
          : ''
      }
      ${
        dcPhotos.length > 0
          ? `
        <div class="photo-grid" style="margin-top: 8px;">
          ${dcPhotos
            .map(
              (photo) => `
            <div class="photo-item">
              <div class="photo-label">${photo.label}</div>
              <div class="photo-container">
                <img src="${photo.base64}" alt="${photo.label}" />
              </div>
            </div>
          `
            )
            .join('')}
        </div>
      `
          : ''
      }
    </div>`;
  }

  private static generateEarthingGroundWorks(
    survey: any,
    photos: any,
    photoBase64Map: Record<string, string>
  ): string {
    const highlighted = survey?.highlightedFields || {};
    const getValue = (
      value: any,
      fieldKey?: string,
      defaultValue: string = 'Not provided'
    ) => PrintService.formatField(value, fieldKey, highlighted, defaultValue);

    // Earth pit location photo
    const earthingPhotos = [];
    if (photoBase64Map['earthPitLocationImage']) {
      earthingPhotos.push({
        type: 'earthPitLocationImage',
        label: 'Earth Pit Location',
        base64: photoBase64Map['earthPitLocationImage'],
      });
    }

    const earthingItems = [
      this.renderInfoItem('Gateway Earthing', survey?.gatewayEarthing, 'gatewayEarthing', highlighted),
      this.renderInfoItem('Ground Tools', survey?.groundTools, 'groundTools', highlighted),
      this.renderInfoItem('Digger Hire', survey?.diggerHire, 'diggerHire', highlighted),
      this.renderInfoItem('Scope of Works (Ground Works)', survey?.scopeOfWorksGroundWorks, undefined, highlighted),
    ].filter(Boolean).join('');

    return `
    <div class="section section-dc">
      <h2 class="section-title">Earthing &amp; Ground Works</h2>
      <div class="info-grid">${earthingItems}</div>
      ${
        earthingPhotos.length > 0
          ? `
        <div class="photo-grid" style="margin-top: 8px;">
          ${earthingPhotos
            .map(
              (photo) => `
            <div class="photo-item">
              <div class="photo-label">${photo.label}</div>
              <div class="photo-container">
                <img src="${photo.base64}" alt="${photo.label}" />
              </div>
            </div>
          `
            )
            .join('')}
        </div>
      `
          : ''
      }
    </div>`;
  }

  private static generateProposedSystem(survey: any): string {
    const highlighted = survey?.highlightedFields || {};
    const getValue = (
      value: any,
      fieldKey?: string,
      defaultValue: string = 'Not provided'
    ) => PrintService.formatField(value, fieldKey, highlighted, defaultValue);

    const proposedItems = [
      this.renderPanelsTable(survey),
      ...(survey?.panelDimensions ? [
        `<div class="info-item">
          <span class="info-label">Panel Dimensions</span>
          <span class="info-value">
            ${survey.panelDimensions.width || '—'} × ${survey.panelDimensions.length || '—'} × ${survey.panelDimensions.depth || '30'} ${survey.panelDimensions.unit || 'mm'}
          </span>
        </div>`,
        this.renderInfoItem('Voc (Open Circuit Voltage)', survey.panelDimensions.voc ? `${survey.panelDimensions.voc} V` : null, 'panelVoc', highlighted),
        this.renderInfoItem('Isc (Short Circuit Current)', survey.panelDimensions.isc ? `${survey.panelDimensions.isc} A` : null, 'panelIsc', highlighted),
      ] : []),
      this.renderInfoItem('Inverter 1', survey?.inverter1, 'inverter1', highlighted),
      this.renderInfoItem('Inverter 2', survey?.inverter2, 'inverter2', highlighted),
      this.renderInfoItem('Inverter 3', survey?.inverter3, 'inverter3', highlighted),
    ].filter(Boolean).join('');

    return `
    <div class="section">
      <h2 class="section-title">Proposed System</h2>
      <div class="info-grid">${proposedItems}</div>
        ${(() => {
          // Get batteries array with fallback to legacy fields
          const batteries = survey?.batteries || [];
          const hasBatteries = Array.isArray(batteries) && batteries.length > 0;

          // Fallback to legacy fields if no batteries array
          const displayBatteries = hasBatteries
            ? batteries
            : (survey?.batteryDescription
                ? [{
                    id: 'legacy',
                    description: survey.batteryDescription,
                    quantity: survey.batteryQuantity || 0,
                    highlighted: highlighted?.batteryDescription || false,
                  }]
                : []);

          if (displayBatteries.length === 0) {
            return `
              <div class="info-item">
                <span class="info-label">Batteries</span>
                <span class="info-value">Not provided</span>
              </div>
            `;
          }

          // Generate batteries table
          const batteriesHtml = displayBatteries.map((battery: any, index: number) => `
            <tr>
              <td style="padding: 8px; border: 1px solid #e5e7eb;">${index + 1}</td>
              <td style="padding: 8px; border: 1px solid #e5e7eb;">
                ${battery.highlighted
                  ? `<mark class="pdf-highlight">${PrintService.formatText(battery.description)}</mark>`
                  : PrintService.formatText(battery.description)}
              </td>
              <td style="padding: 8px; border: 1px solid #e5e7eb; text-align: center;">${battery.quantity || 0}</td>
            </tr>
          `).join('');

          return `
            <div class="info-item" style="grid-column: span 2;">
              <span class="info-label">Battery Storage</span>
              <table style="width: 100%; border-collapse: collapse; margin-top: 8px;">
                <thead>
                  <tr style="background-color: #f9fafb;">
                    <th style="padding: 8px; border: 1px solid #e5e7eb; text-align: left; width: 50px;">#</th>
                    <th style="padding: 8px; border: 1px solid #e5e7eb; text-align: left;">Battery Type</th>
                    <th style="padding: 8px; border: 1px solid #e5e7eb; text-align: center; width: 100px;">Quantity</th>
                  </tr>
                </thead>
                <tbody>
                  ${batteriesHtml}
                </tbody>
              </table>
            </div>
          `;
        })()}
      </div>
    </div>`;
  }

  private static generateSurveyMetadata(survey: any): string {
    const getValue = (value: any, defaultValue: string = 'Not provided') =>
      value ? PrintService.formatText(value) : defaultValue;

    // Only show this section if we have GPS data or survey-specific address
    const hasGPS = survey?.surveyLatitude && survey?.surveyLongitude;
    const hasSurveyAddress = survey?.surveyAddress && survey?.surveyAddress !== '';

    if (!hasGPS && !hasSurveyAddress) {
      return '';
    }

    return `
    <div class="section">
      <h2 class="section-title">Survey Location Details</h2>
      <div class="info-grid">
        ${
          hasSurveyAddress
            ? `
        <div class="info-item">
          <span class="info-label">Survey Address</span>
          <span class="info-value">${getValue(survey?.surveyAddress)}</span>
        </div>
        `
            : ''
        }
        ${
          hasGPS
            ? `
        <div class="info-item">
          <span class="info-label">GPS Coordinates</span>
          <span class="info-value">Lat: ${survey.surveyLatitude.toFixed(6)}, Long: ${survey.surveyLongitude.toFixed(6)}</span>
        </div>
        `
            : ''
        }
      </div>
    </div>`;
  }

  private static generateNotesObservations(survey: any): string {
    const hasSpecialRequirements =
      survey?.specialRequirements && survey.specialRequirements.trim() !== '';

    if (!hasSpecialRequirements) {
      return '';
    }

    return `
    <div class="section">
      <h2 class="section-title">Special Requirements / Notes</h2>
      <div class="notes-section">
        <div class="notes-content">${PrintService.formatText(survey.specialRequirements)}</div>
      </div>
    </div>`;
  }

  private static generatePhotoGalleries(
    photos: any,
    photoBase64Map: Record<string, string>
  ): string {
    if (!photos || Object.keys(photos).length === 0) {
      return '';
    }

    const photoCategories: Record<
      string,
      Array<{ type: string; label: string; base64?: string }>
    > = {
      Property: [],
      'Meter & Electrical': [],
      Roof: [],
      'Mounting & Access': [],
    };

    // Categorize photos
    Object.entries(photos).forEach(([type, photo]: [string, any]) => {
      const label = this.getPhotoLabel(photo.photoType || type);
      const base64 = photoBase64Map[type];

      if (type.includes('property') || type.includes('front') || type.includes('rear')) {
        photoCategories['Property'].push({ type, label, base64 });
      } else if (
        type.includes('meter') ||
        type.includes('consumer') ||
        type.includes('electrical')
      ) {
        photoCategories['Meter & Electrical'].push({ type, label, base64 });
      } else if (type.includes('roof')) {
        photoCategories['Roof'].push({ type, label, base64 });
      } else {
        photoCategories['Mounting & Access'].push({ type, label, base64 });
      }
    });

    let html = '';

    for (const [category, categoryPhotos] of Object.entries(photoCategories)) {
      if (categoryPhotos.length === 0) continue;

      html += `
      <div class="section photo-gallery">
        <h2 class="section-title">${category} Photos</h2>
        <div class="photo-grid">`;

      for (const photo of categoryPhotos) {
        html += `
          <div class="photo-item">
            <div class="photo-label">${photo.label}</div>
            <div class="photo-container">
              ${
                photo.base64
                  ? `<img src="${photo.base64}" alt="${photo.label}" />`
                  : '<div class="photo-placeholder">No image available</div>'
              }
            </div>
          </div>`;
      }

      html += `
        </div>
      </div>`;
    }

    return html;
  }

  private static getPhotoLabel(photoType: string): string {
    const labels: Record<string, string> = {
      // Property photos
      property_front: 'Property Front View',
      property_rear: 'Property Rear View',
      property_side: 'Property Side View',

      // General project photos
      stringPlanFromOpenSolar: 'String Plan (OpenSolar)',
      aerialImageFromDrone: 'Aerial View (Drone)',
      generalImage1: 'General Photo 1',
      generalImage2: 'General Photo 2',
      generalImage3: 'General Photo 3',

      // Electrical photos
      photoIncomer: 'Incomer',
      photoMeter: 'Meter',
      photoMainDb: 'Main DB',
      photoOther: 'Other Electrical',
      meter_location: 'Meter Location',
      meter_close_up: 'Meter Close-up',
      consumer_unit: 'Consumer Unit',
      consumer_unit_open: 'Consumer Unit (Open)',

      // Roof photos
      roof_front: 'Roof Front View',
      roof_rear: 'Roof Rear View',
      roof_side: 'Roof Side View',
      roof_close_up: 'Roof Close-up',
      roof_obstacles: 'Roof Obstacles',
      proposed_panel_area: 'Proposed Panel Area',
      scaffoldPlanImage: 'Scaffold Plan',

      // Equipment location photos
      proposedAreaPhoto: 'Proposed Area',
      inverter_location: 'Inverter Location',
      battery_location: 'Battery Location',

      // Cable route photos
      cable_route_exterior: 'Cable Route (Exterior)',
      cable_route_interior: 'Cable Route (Interior)',
      cableRouteImage1: 'Cable Route 1',
      cableRouteImage2: 'Cable Route 2',

      // DC route photos
      dcRouteImage1: 'DC Route 1',
      dcRouteImage2: 'DC Route 2',
      dcRouteImage3: 'DC Route 3',

      // Earthing photos
      earthPitLocationImage: 'Earth Pit Location',

      // Safety photos
      access_equipment: 'Access Equipment',
      electrical_hazards: 'Electrical Hazards',

      // Additional photos
      additional_1: 'Additional Photo 1',
      additional_2: 'Additional Photo 2',
      additional_3: 'Additional Photo 3',
    };

    return (
      labels[photoType] || photoType.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())
    );
  }

  private static generateFooter(): string {
    return ''; // No footer needed
  }

  /**
   * Print survey using browser's native print dialog
   */
  static async printSurvey(
    surveyId: string,
    onProgress?: (progress: number, message: string) => void,
    overrides?: { highlightedFields?: Record<string, boolean> }
  ): Promise<void> {
    let loadingToast: HTMLDivElement | null = null;
    let printFrame: HTMLIFrameElement | null = null;
    let isPrintDialogOpen = false;

    try {
      console.log('[PrintService] Starting print process for survey:', surveyId);

      // Progress callback helper
      const updateProgress = (progress: number, message: string) => {
        onProgress?.(progress, message);
        if (loadingToast) {
          loadingToast.innerHTML = `
            <div>${message}</div>
            <div style="margin-top: 4px; background: rgba(255,255,255,0.2); border-radius: 2px; height: 4px;">
              <div style="background: white; height: 100%; width: ${progress}%; border-radius: 2px; transition: width 0.3s;"></div>
            </div>
          `;
        }
      };

      // Show loading indicator with progress bar
      if (!onProgress) {
        loadingToast = document.createElement('div');
        loadingToast.style.cssText =
          'position:fixed;top:20px;right:20px;background:#333;color:white;padding:12px 20px;border-radius:4px;z-index:10000;min-width:250px';
        document.body.appendChild(loadingToast);
      }

      updateProgress(5, 'Fetching survey data...');

      // Fetch survey data
      const data = await this.fetchSurveyData(surveyId);

      // Merge in overrides (e.g., frontend highlightedFields not stored in DB)
      const surveyWithOverrides = {
        ...data.survey,
        highlightedFields: overrides?.highlightedFields || (data.survey as any)?.highlightedFields || {},
      } as any;
      const mergedData = { ...data, survey: surveyWithOverrides } as SurveyPrintData;
      console.log('[PrintService] Survey data fetched successfully');

      updateProgress(10, 'Processing images for print...');

      // Generate HTML with embedded images and progress tracking
      const html = await this.generatePrintHTML(mergedData, updateProgress);
      console.log('[PrintService] HTML generated, length:', html.length);

      updateProgress(95, 'Opening print dialog...');

      // Create invisible iframe for printing
      printFrame = document.createElement('iframe');
      printFrame.style.cssText =
        'position:absolute;left:-9999px;top:-9999px;width:0;height:0;opacity:0';
      printFrame.name = `print-frame-${Date.now()}`; // Unique name to prevent conflicts
      document.body.appendChild(printFrame);

      // Write HTML to iframe
      const frameDoc = printFrame.contentDocument || printFrame.contentWindow?.document;
      if (!frameDoc) {
        throw new Error('Unable to create print document');
      }

      frameDoc.open();
      frameDoc.write(html);
      frameDoc.close();

      // Remove loading indicator before showing print dialog
      if (loadingToast && document.body.contains(loadingToast)) {
        document.body.removeChild(loadingToast);
        loadingToast = null;
      }

      updateProgress(100, 'Ready to print');

      // Setup print function with debouncing to prevent double triggering
      const triggerPrint = () => {
        if (isPrintDialogOpen || !printFrame?.contentWindow) return;

        isPrintDialogOpen = true;
        console.log('[PrintService] Opening print dialog');

        try {
          printFrame.contentWindow.focus();
          printFrame.contentWindow.print();
        } catch (error) {
          console.error('[PrintService] Error triggering print:', error);
          isPrintDialogOpen = false;
        }

        // Clean up iframe after a delay
        setTimeout(() => {
          if (printFrame && document.body.contains(printFrame)) {
            document.body.removeChild(printFrame);
          }
          isPrintDialogOpen = false;
        }, 2000);
      };

      // Wait for iframe to be ready, then print
      // Don't set onload and manually trigger - just wait for natural load
      if (frameDoc.readyState === 'complete') {
        // Document is already loaded, print after a short delay
        setTimeout(triggerPrint, 200);
      } else {
        // Wait for document to load naturally
        printFrame.onload = () => {
          console.log('[PrintService] Print frame loaded naturally');
          setTimeout(triggerPrint, 200);
        };
      }
    } catch (error) {
      console.error('[PrintService] Print failed with error:', error);
      console.error('[PrintService] Error details:', {
        message: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : undefined,
      });

      // Clean up on error
      if (loadingToast && document.body.contains(loadingToast)) {
        document.body.removeChild(loadingToast);
      }
      if (printFrame && document.body.contains(printFrame)) {
        document.body.removeChild(printFrame);
      }

      // Show more detailed error message
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      alert(
        `Failed to prepare survey for printing: ${errorMessage}\n\nPlease check the console for more details.`
      );

      // Re-throw the error so it's visible in the console
      throw error;
    }
  }
}
